import React, { useCallback, useMemo, useEffect } from "react";
import { TextField, RadioButtonGroup, Dropdown } from "..";
import useLocationFormatter from "hooks/useLocationFormatter";

interface CoordinatesValue {
  direction?: string;
  degrees?: string;
  minutes?: string;
  seconds?: string;
}

interface GpsData {
  CoordinatesFormat: string;
  Latitude: CoordinatesValue | number;
  Longitude: CoordinatesValue | number;
}

interface GpsCoordinatesProps {
  value: GpsData;
  onChange: (data: GpsData) => void;
  editMode: boolean;
  elements: any[];
  className?: string;
  disabled?: boolean;
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
  sectionId?: string;
  panelConfig?: any;
  panel?: any;
}

const COORDINATE_FORMATS = {
  DECIMAL_DEGREES: "Decimal degrees",
  DEGREES_MINUTES_SECONDS: "Degrees, minutes, seconds",
  DEGREES_DECIMAL_MINUTES: "Degrees, decimal minutes",
} as const;

const COORDINATE_LIMITS = {
  LATITUDE: { min: -90, max: 90 },
  LONGITUDE: { min: -180, max: 180 },
  DEGREES: { min: 0, max: 360 },
  MINUTES: { min: 0, max: 59.999999 },
  SECONDS: { min: 0, max: 59.999999 },
} as const;

const GpsCoordinates: React.FC<GpsCoordinatesProps> = ({
  value = { CoordinatesFormat: "", Latitude: 0, Longitude: 0 },
  onChange,
  editMode,
  elements,
  className = "",
  disabled = false,
  onValidationChange,
  sectionId,
  panelConfig,
  panel = {},
}) => {
  const { formatLocation } = useLocationFormatter();

  /* ---------- conversions (stable) ---------- */
  const conversionUtils = useMemo(
    () => ({
      convertDMS2DD: (
        degrees: string | number,
        direction: string,
        minutes: string | number,
        seconds: string | number = "0"
      ): number => {
        const deg = Number(degrees) || 0;
        const min = Number(minutes) || 0;
        const sec = Number(seconds) || 0;
        let decimalDegrees = deg + min / 60 + sec / 3600;
        if (direction === "S" || direction === "W")
          decimalDegrees = -decimalDegrees;
        return Math.round(decimalDegrees * 1e6) / 1e6;
      },

      convertDD2DMS: (decimalDegrees: number, isLongitude = false) => {
        const direction = isLongitude
          ? decimalDegrees >= 0
            ? "E"
            : "W"
          : decimalDegrees >= 0
            ? "N"
            : "S";
        const abs = Math.abs(decimalDegrees);
        const degrees = Math.floor(abs);
        const minutesFromDecimal = (abs - degrees) * 60;
        const minutes = Math.floor(minutesFromDecimal);
        const seconds =
          Math.round((minutesFromDecimal - minutes) * 60 * 1000) / 1000;
        return { degrees, minutes, seconds, direction };
      },

      formatDecimal: (val: number, decimals = 3) => {
        if (typeof val !== "number" || isNaN(val)) return "0";
        const rounded =
          Math.round(val * Math.pow(10, decimals)) / Math.pow(10, decimals);
        return rounded % 1 === 0 ? String(rounded) : rounded.toFixed(decimals);
      },
    }),
    []
  );

  /* ---------- validation ---------- */
  const validateCoordinates = useCallback((data: GpsData): string[] => {
    const errors: string[] = [];
    const { CoordinatesFormat, Latitude, Longitude } = data;

    if (!CoordinatesFormat) errors.push("Coordinates format is required");

    // Latitude
    if (typeof Latitude === "number") {
      if (
        Latitude < COORDINATE_LIMITS.LATITUDE.min ||
        Latitude > COORDINATE_LIMITS.LATITUDE.max
      ) {
        errors.push(
          `Latitude must be between ${COORDINATE_LIMITS.LATITUDE.min} and ${COORDINATE_LIMITS.LATITUDE.max}`
        );
      }
    } else if (Latitude) {
      if (!Latitude.direction || !Latitude.degrees || !Latitude.minutes) {
        errors.push("Latitude direction, degrees, and minutes are required");
      }
      const deg = Number(Latitude.degrees);
      const min = Number(Latitude.minutes);
      const sec = Number(Latitude.seconds || 0);
      if (deg > 90) errors.push("Latitude degrees cannot exceed 90");
      if (min >= 60) errors.push("Latitude minutes must be less than 60");
      if (sec >= 60) errors.push("Latitude seconds must be less than 60");
    }

    // Longitude
    if (typeof Longitude === "number") {
      if (
        Longitude < COORDINATE_LIMITS.LONGITUDE.min ||
        Longitude > COORDINATE_LIMITS.LONGITUDE.max
      ) {
        errors.push(
          `Longitude must be between ${COORDINATE_LIMITS.LONGITUDE.min} and ${COORDINATE_LIMITS.LONGITUDE.max}`
        );
      }
    } else if (Longitude) {
      if (!Longitude.direction || !Longitude.degrees || !Longitude.minutes) {
        errors.push("Longitude direction, degrees, and minutes are required");
      }
      const deg = Number(Longitude.degrees);
      const min = Number(Longitude.minutes);
      const sec = Number(Longitude.seconds || 0);
      if (deg > 180) errors.push("Longitude degrees cannot exceed 180");
      if (min >= 60) errors.push("Longitude minutes must be less than 60");
      if (sec >= 60) errors.push("Longitude seconds must be less than 60");
    }

    return errors;
  }, []);

  const validationErrors = useMemo(
    () => validateCoordinates(value),
    [validateCoordinates, value]
  );
  useEffect(() => {
    onValidationChange?.(validationErrors.length === 0, validationErrors);
  }, [validationErrors, onValidationChange]);

  /* ---------- formatting for placeholders ---------- */
  const getFormattedCoordinates = useCallback(
    (latitude: any, longitude: any, format: "DMS" | "DD" | "DMM"): string => {
      try {
        if (!latitude || !longitude) return "Coordinates not available";
        const { formatDecimal } = conversionUtils;

        switch (format) {
          case "DMS":
            if (typeof latitude !== "object") {
              return `${formatLocation(latitude, format, false, "DDMS")}, ${formatLocation(longitude, format, true, "DDMS")}`;
            }
            return `${latitude.direction} ${formatDecimal(Number(latitude.degrees))}° ${formatDecimal(Number(latitude.minutes))}' ${formatDecimal(Number(latitude.seconds || 0))}", ${longitude.direction} ${formatDecimal(Number(longitude.degrees))}° ${formatDecimal(Number(longitude.minutes))}' ${formatDecimal(Number(longitude.seconds || 0))}"`;
          case "DMM":
            if (typeof latitude !== "object") {
              return `${formatLocation(latitude, format, false, "DDMM")}, ${formatLocation(longitude, format, true, "DDMM")}`;
            } else {
              const latMin =
                Number(latitude.minutes) +
                (latitude.seconds ? Number(latitude.seconds) / 60 : 0);
              const lonMin =
                Number(longitude.minutes) +
                (longitude.seconds ? Number(longitude.seconds) / 60 : 0);
              return `${latitude.direction} ${formatDecimal(Number(latitude.degrees))}° ${formatDecimal(latMin)}', ${longitude.direction} ${formatDecimal(Number(longitude.degrees))}° ${formatDecimal(lonMin)}'`;
            }
          case "DD":
            return "Pick on map / get GPS data";
          default:
            return "Unknown format";
        }
      } catch {
        return "Error formatting coordinates";
      }
    },
    [conversionUtils, formatLocation]
  );

  /* ---------- conversions between formats (pure) ---------- */
  const convertCoordinates = useCallback(
    (currentData: GpsData, newFormat: string): GpsData => {
      const { Latitude, Longitude } = currentData;
      const { convertDMS2DD, convertDD2DMS } = conversionUtils;

      switch (newFormat) {
        case COORDINATE_FORMATS.DECIMAL_DEGREES: {
          const latDD =
            typeof Latitude === "object" && Latitude
              ? convertDMS2DD(
                  Latitude.degrees || "0",
                  Latitude.direction || "N",
                  Latitude.minutes || "0",
                  Latitude.seconds || "0"
                )
              : Number(Latitude) || 0;
          const lngDD =
            typeof Longitude === "object" && Longitude
              ? convertDMS2DD(
                  Longitude.degrees || "0",
                  Longitude.direction || "E",
                  Longitude.minutes || "0",
                  Longitude.seconds || "0"
                )
              : Number(Longitude) || 0;
          return {
            CoordinatesFormat: newFormat,
            Latitude: latDD,
            Longitude: lngDD,
          };
        }

        case COORDINATE_FORMATS.DEGREES_MINUTES_SECONDS: {
          const latDMS =
            typeof Latitude === "object" && Latitude
              ? { ...Latitude, seconds: Latitude.seconds ?? "0" }
              : (() => {
                  const c = convertDD2DMS(Number(Latitude) || 0, false);
                  return {
                    direction: c.direction,
                    degrees: String(c.degrees),
                    minutes: String(c.minutes),
                    seconds: String(c.seconds),
                  };
                })();

          const lngDMS =
            typeof Longitude === "object" && Longitude
              ? { ...Longitude, seconds: Longitude.seconds ?? "0" }
              : (() => {
                  const c = convertDD2DMS(Number(Longitude) || 0, true);
                  return {
                    direction: c.direction,
                    degrees: String(c.degrees),
                    minutes: String(c.minutes),
                    seconds: String(c.seconds),
                  };
                })();

          return {
            CoordinatesFormat: newFormat,
            Latitude: latDMS,
            Longitude: lngDMS,
          };
        }

        case COORDINATE_FORMATS.DEGREES_DECIMAL_MINUTES: {
          const latDMM =
            typeof Latitude === "object" && Latitude
              ? {
                  direction: Latitude.direction,
                  degrees: Latitude.degrees,
                  minutes: String(
                    Number(Latitude.minutes || 0) +
                      (Latitude.seconds ? Number(Latitude.seconds) / 60 : 0)
                  ),
                }
              : (() => {
                  const c = convertDD2DMS(Number(Latitude) || 0, false);
                  return {
                    direction: c.direction,
                    degrees: String(c.degrees),
                    minutes: String(c.minutes + c.seconds / 60),
                  };
                })();

          const lngDMM =
            typeof Longitude === "object" && Longitude
              ? {
                  direction: Longitude.direction,
                  degrees: Longitude.degrees,
                  minutes: String(
                    Number(Longitude.minutes || 0) +
                      (Longitude.seconds ? Number(Longitude.seconds) / 60 : 0)
                  ),
                }
              : (() => {
                  const c = convertDD2DMS(Number(Longitude) || 0, true);
                  return {
                    direction: c.direction,
                    degrees: String(c.degrees),
                    minutes: String(c.minutes + c.seconds / 60),
                  };
                })();

          return {
            CoordinatesFormat: newFormat,
            Latitude: latDMM,
            Longitude: lngDMM,
          };
        }

        default:
          return currentData;
      }
    },
    [conversionUtils]
  );

  /* ---------- handlers (no internal state; clone value ➜ onChange) ---------- */
  const handleFormatChange = useCallback(
    (newFormat: any) => {
      if (disabled) return;
      console.log("hello", newFormat.value);
      const converted = convertCoordinates(value, newFormat.value);

      console.log("converted", converted);
      onChange(converted);
    },
    [convertCoordinates, disabled, onChange, value]
  );

  const handleCoordinateChange = useCallback(
    (coordinateType: "Latitude" | "Longitude", nextValue: any) => {
      if (disabled) return;
      const next: GpsData = { ...value, [coordinateType]: nextValue };
      onChange(next);
    },
    [disabled, onChange, value]
  );

  const handleDMSChange = useCallback(
    (coordinateType: "Latitude" | "Longitude", part: string, val: any) => {
      if (disabled) return;

      const sanitizedVal = String(val).replace(/-/g, "");
      const current = (value[coordinateType] as CoordinatesValue) || {};
      const candidate: CoordinatesValue = { ...current, [part]: sanitizedVal };

      // component-level limits
      const deg = Number(candidate.degrees || 0);
      const min = Number(candidate.minutes || 0);
      const sec = Number(candidate.seconds || 0);

      if (
        part === "degrees" &&
        deg > (coordinateType === "Latitude" ? 90 : 180)
      )
        return;
      if (part === "minutes" && min >= 60) return;
      if (part === "seconds" && sec >= 60) return;

      // total degrees validation (honor direction)
      const minutesInDegrees = min / 60;
      const secondsInDegrees = sec / 3600;
      let totalDegrees = deg + minutesInDegrees + secondsInDegrees;
      if (candidate.direction === "S" || candidate.direction === "W")
        totalDegrees = -totalDegrees;
      const limit = coordinateType === "Latitude" ? 90 : 180;
      if (Math.abs(totalDegrees) > limit) return;

      // DMM format does not store seconds
      if (
        value.CoordinatesFormat === COORDINATE_FORMATS.DEGREES_DECIMAL_MINUTES
      ) {
        delete candidate.seconds;
      }

      onChange({ ...value, [coordinateType]: candidate });
    },
    [disabled, onChange, value]
  );

  /* ---------- UI data ---------- */
  const formatChoices = useMemo(() => {
    try {
      const el = elements.find((e) => e?.name === "CoordinatesFormat");
      return el?.choices || [];
    } catch {
      return [];
    }
  }, [elements]);

  const coordinatesPlaceholders = useMemo(() => {
    return formatChoices.map((f: any) => {
      const formatValue = f?.value || f;
      const display =
        formatValue === COORDINATE_FORMATS.DEGREES_MINUTES_SECONDS
          ? "DMS"
          : formatValue === COORDINATE_FORMATS.DECIMAL_DEGREES
            ? "DD"
            : "DMM";
      return getFormattedCoordinates(
        value.Latitude,
        value.Longitude,
        display as any
      );
    });
  }, [formatChoices, getFormattedCoordinates, value.Latitude, value.Longitude]);

  /* ---------- field renderer ---------- */
  const renderCoordinateField = useCallback(
    (coordinateType: "Latitude" | "Longitude") => {
      const isLat = coordinateType === "Latitude";
      const title = isLat ? "Latitude" : "Longitude";
      const directions = isLat ? ["N", "S"] : ["E", "W"];
      const current = value[coordinateType];
      const fmt = value.CoordinatesFormat;

      const isDMS = fmt === COORDINATE_FORMATS.DEGREES_MINUTES_SECONDS;
      const isDMM = fmt === COORDINATE_FORMATS.DEGREES_DECIMAL_MINUTES;
      const isDD = fmt === COORDINATE_FORMATS.DECIMAL_DEGREES;

      if (isDD) {
        return (
          <div className="col-span-6" key={coordinateType}>
            <TextField
              label={title}
              type="number"
              name={coordinateType.toLowerCase()}
              value={current as number}
              handleChange={(nv: any) => {
                if (nv === "") {
                  handleCoordinateChange(coordinateType, 0);
                  return;
                }
                const parsed = parseFloat(nv);
                if (!isNaN(parsed)) {
                  const lim = isLat
                    ? COORDINATE_LIMITS.LATITUDE
                    : COORDINATE_LIMITS.LONGITUDE;
                  if (parsed < lim.min || parsed > lim.max) return;
                }
                handleCoordinateChange(coordinateType, nv);
              }}
              editMode={editMode}
              placeholder={`Enter ${title} (${isLat ? "-90 to 90" : "-180 to 180"})`}
              required
              disabled={disabled}
              panel={panel || {}}
              element={{}}
              aria-label={`${title} in decimal degrees`}
            />
          </div>
        );
      }

      const coordValue = (current as CoordinatesValue) || {};

      return (
        <div
          className="col-span-6  items-end bg-bgPrimaryLight p-4 rounded-lg"
          key={coordinateType}
        >
          <p
            className={`w-full pb-1 text-left capitalize text-md font-Overpass ${editMode ? "font-semibold text-primary" : "font-regular text-primary"} dark:text-caption`}
          >
            {title}{" "}
            <span className="text-red-500" aria-label="required">
              *
            </span>
          </p>

          {editMode ? (
            <div
              className={`items-start lat-lng-fields gap-2 grid grid-cols-${isDMS ? "4" : "3"}`}
              role="group"
              aria-label={`${title} coordinates input`}
            >
              <Dropdown
                name="direction"
                label="Direction"
                choices={directions}
                value={coordValue?.direction}
                handleChange={(item: any) =>
                  handleDMSChange(coordinateType, "direction", item)
                }
                editMode={editMode}
                required
                disabled={disabled}
                error={!coordValue?.direction && "Direction is required"}
                touched
                labelSize="sm"
                panel={panelConfig || {}}
                element={{}}
                aria-label={`${title} direction`}
                choicesByUrl={null}
              />

              <TextField
                type="number"
                name="degrees"
                label="Degrees"
                value={
                  coordValue?.degrees ? parseFloat(coordValue.degrees) : ""
                }
                handleChange={(val: any) =>
                  handleDMSChange(coordinateType, "degrees", val)
                }
                editMode={editMode}
                placeholder={isLat ? "0–90" : "0–180"}
                required
                disabled={disabled}
                error={!coordValue?.degrees && "Degrees are required"}
                touched
                labelSize="sm"
                panel={panel}
                element={{}}
                aria-label={`${title} degrees`}
              />

              <TextField
                type="number"
                name="minutes"
                label="Minutes"
                value={
                  coordValue?.minutes
                    ? parseFloat(coordValue.minutes) +
                      (isDMM && coordValue?.seconds
                        ? parseFloat(coordValue.seconds) / 60
                        : 0)
                    : ""
                }
                handleChange={(val: any) =>
                  handleDMSChange(coordinateType, "minutes", val)
                }
                editMode={editMode}
                placeholder={isDMM ? "0–60 (decimal)" : "0–59"}
                required
                disabled={disabled}
                error={!coordValue?.minutes && "Minutes are required"}
                touched
                labelSize="sm"
                panel={panel}
                element={{}}
                aria-label={`${title} minutes`}
              />

              {isDMS && (
                <TextField
                  type="number"
                  name="seconds"
                  label="Seconds"
                  value={
                    coordValue?.seconds ? parseFloat(coordValue.seconds) : ""
                  }
                  handleChange={(val: any) =>
                    handleDMSChange(coordinateType, "seconds", val)
                  }
                  editMode={editMode}
                  placeholder="0–59"
                  required
                  disabled={disabled}
                  error={!coordValue?.seconds && "Seconds are required"}
                  touched
                  labelSize="sm"
                  panel={panel || {}}
                  element={{}}
                  aria-label={`${title} seconds`}
                />
              )}
            </div>
          ) : (
            <div className="relative flex items-center self-stretch justify-start">
              <p
                className="w-full max-w-full max-h-32 overflow-hidden text-overflow-ellipsis whitespace-pre-line text-[15px] font-Overpass text-left font-normal text-textAnswer dark:text-textMain"
                aria-label={`${title} value`}
              >
                {coordValue?.direction &&
                coordValue?.degrees &&
                coordValue?.minutes
                  ? conversionUtils.convertDMS2DD(
                      coordValue.degrees,
                      coordValue.direction,
                      coordValue.minutes,
                      coordValue.seconds
                    )
                  : "Not set"}
              </p>
            </div>
          )}
        </div>
      );
    },
    [
      conversionUtils,
      disabled,
      editMode,
      handleCoordinateChange,
      handleDMSChange,
      panel,
      panelConfig,
      value,
    ]
  );

  /* ---------- render ---------- */
  return (
    <div
      className={` col-span-12  ${className} ${disabled ? "opacity-50 pointer-events-none" : ""}`}
    >
      {/* Validation list (derived; no local state) */}
      {/* {validationErrors.length > 0 && (
        <div
          className="col-span-12 p-3 bg-red-50 border border-red-200 rounded-md"
          role="alert"
        >
          <h4 className="text-sm font-medium text-red-800 mb-1">
            Validation Errors:
          </h4>
          <ul className="text-xs text-red-700 space-y-1">
            {validationErrors.map((e, i) => (
              <li key={i}>• {e}</li>
            ))}
          </ul>
        </div>
      )} */}

      {/* Format selector */}
      <div className="!col-span-12">
        <RadioButtonGroup
          label="Coordinates Format"
          name="coordinatesFormat"
          editMode={editMode}
          value={value?.CoordinatesFormat}
          handleChange={handleFormatChange}
          data={formatChoices}
          required
          disabled={disabled}
          dataPlaceholders={coordinatesPlaceholders}
          panel={panelConfig || {}}
          element={formatChoices[0] || {}}
          aria-label="Select coordinate format"
        />
      </div>

      {/* Fields */}
      <div className="grid grid-cols-12 gap-4 mt-4">
        {renderCoordinateField("Latitude")}
        {renderCoordinateField("Longitude")}
      </div>
    </div>
  );
};

export default React.memo(GpsCoordinates);
